/**
 * The one process-wide upload manager for the Image Asset Center
 * (VistoeraTASK-00522) + its React hook. Module scope => it outlives the page
 * and the upload modal (SPA navigation never stops an upload).
 */
import { useMemo, useSyncExternalStore } from 'react'
import { UploadManager, type UploadBatchView } from './uploadManager'
import { defaultUploadTransport } from './uploadTransport'

function onBeforeUnload(e: BeforeUnloadEvent) {
  e.preventDefault()
  // Legacy browsers need a returnValue to show the confirmation.
  e.returnValue = ''
}

let guardInstalled = false
function setUnloadGuard(active: boolean): void {
  if (typeof window === 'undefined') return
  if (active && !guardInstalled) {
    window.addEventListener('beforeunload', onBeforeUnload)
    guardInstalled = true
  } else if (!active && guardInstalled) {
    window.removeEventListener('beforeunload', onBeforeUnload)
    guardInstalled = false
  }
}

export const imageUploadManager = new UploadManager({
  transport: defaultUploadTransport,
  onActiveChange: setUnloadGuard,
})

/** Live batches of ONE Workspace (never mixes Workspaces). */
export function useWorkspaceUploadBatches(workspaceId: string): UploadBatchView[] {
  const snapshot = useSyncExternalStore(imageUploadManager.subscribe, imageUploadManager.getSnapshot)
  return useMemo(() => snapshot.batches.filter((b) => b.workspaceId === workspaceId), [snapshot, workspaceId])
}
